import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  localDate,
  overlayRun,
  overlayStops,
  runProblems,
  watchDocsById,
  watchMyClosedRuns,
  watchMyCustody,
  watchMyOpenRuns,
  watchMyBoxes,
  watchRunStops,
  type CommandProblem,
  type OpsCustody,
  type OpsPickupRequest,
  type OpsRun,
  type OpsShipment,
  type OpsStop,
  type RunBox,
  type RunView,
  type StopView,
} from '@ecobox/firebase';
import { useNow } from '@ecobox/ui';
import { useLedger, useServices, useSession } from '../services';
import { usePermissions } from '../sessions';
import { useWatch } from './useWatch';
import { usePickupConfig, type PickupConfig } from './usePickupConfig';

const NO_RUNS: OpsRun[] = [];
const NO_BOXES: RunBox[] = [];
const NO_CUSTODY: OpsCustody[] = [];
const NO_SHIPMENTS: ReadonlyMap<string, OpsShipment> = new Map();
const NO_PICKUPS: ReadonlyMap<string, OpsPickupRequest> = new Map();

export interface DriverData {
  /** The person can deliver or complete pickups (`finalmile.deliver` / `pickup.complete`). */
  isDriver: boolean;
  today: string;
  /** Open runs and the runs closed today, with the commands waiting on this phone applied. */
  runs: RunView[];
  loading: boolean;
  error: string | null;
  stopsOf(runId: string): StopView[] | undefined;
  boxesOf(runId: string): readonly RunBox[];
  shipments: ReadonlyMap<string, OpsShipment>;
  pickups: ReadonlyMap<string, OpsPickupRequest>;
  custody: readonly OpsCustody[];
  /** Commands of a run the server refused or never received. */
  problemsOf(runId: string): CommandProblem[];
  /** A run of mine is in progress: the heartbeat then sends the van's position. */
  hasActiveRun: boolean;
  config: PickupConfig;
}

const DriverContext = createContext<DriverData | null>(null);

/** The driver data, or null where there is no provider (a screen shown on its own). */
export function useOptionalDriverData(): DriverData | null {
  return useContext(DriverContext);
}

export function useDriverData(): DriverData {
  const value = useContext(DriverContext);
  if (!value) throw new Error('DriverDataProvider is missing.');
  return value;
}

/** A live query per key, kept in a map. Keys that go away are unsubscribed; each key starts once. */
function useKeyedWatch<T>(
  keys: readonly string[],
  start: (key: string, onData: (value: T) => void, onError: (err: Error) => void) => () => void,
): { data: ReadonlyMap<string, T>; error: string | null } {
  const [data, setData] = useState<ReadonlyMap<string, T>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const joined = [...new Set(keys)].sort().join('|');
  const startRef = useRef(start);
  useEffect(() => {
    startRef.current = start;
  });
  useEffect(() => {
    const list = joined === '' ? [] : joined.split('|');
    const stops = list.map((key) =>
      startRef.current(
        key,
        (value) => setData((m) => new Map(m).set(key, value)),
        (err) => setError(err.message),
      ),
    );
    return () => {
      for (const stop of stops) stop();
    };
  }, [joined]);
  // A run that left the list is dropped from what is handed on (its listener is already stopped).
  const current = useMemo(() => {
    const wanted = new Set(joined === '' ? [] : joined.split('|'));
    return new Map([...data].filter(([k]) => wanted.has(k)));
  }, [data, joined]);
  return { data: current, error };
}

/** Follows documents by id (shipments, pickup requests), one answer map for all of them. */
function useDocMap<T>(
  collectionName: 'shipments' | 'pickupRequests',
  ids: readonly string[],
  idField: string,
  empty: ReadonlyMap<string, T>,
): ReadonlyMap<string, T> {
  const { db } = useServices().client;
  const joined = [...new Set(ids)].sort().join('|');
  const start = useCallback(
    (onData: (rows: ReadonlyMap<string, T>) => void, onError: (err: Error) => void) =>
      watchDocsById<T>(
        db,
        collectionName,
        joined === '' ? [] : joined.split('|'),
        idField,
        onData,
        onError,
      ),
    [db, collectionName, joined, idField],
  );
  // A missing or unreadable document is not fatal: the stop still shows its label.
  const state = useWatch(joined === '' ? null : start, empty);
  return joined === '' ? empty : state.data;
}

/**
 * Everything a driver reads, shared by Today, the run, the stops and the heartbeat, so each screen
 * opens at once and the Firestore cache already holds the working set when the signal drops
 * (02 §2.2 "Offline working set": today's and tomorrow's runs and stops, their shipments and boxes,
 * the rate cards and box sizes). Does nothing for people who are not drivers.
 */
export function DriverDataProvider({ children }: { children: ReactNode }) {
  const { db } = useServices().client;
  const { uid } = useSession();
  const perms = usePermissions();
  const entries = useLedger();
  const isDriver = perms.has('finalmile.deliver') || perms.has('pickup.complete');
  const canPickup = perms.has('pickup.complete');
  const now = useNow(60_000);
  const today = localDate(now);
  const tomorrow = localDate(now + 86_400_000);

  const live = isDriver && uid !== null;
  const startOpen = useCallback(
    (d: (v: OpsRun[]) => void, e: (err: Error) => void) => watchMyOpenRuns(db, uid ?? '', d, e),
    [db, uid],
  );
  const startClosed = useCallback(
    (d: (v: OpsRun[]) => void, e: (err: Error) => void) =>
      watchMyClosedRuns(db, { uid: uid ?? '', date: today }, d, e),
    [db, uid, today],
  );
  const startCustody = useCallback(
    (d: (v: OpsCustody[]) => void, e: (err: Error) => void) => watchMyCustody(db, uid ?? '', d, e),
    [db, uid],
  );
  const open = useWatch(live ? startOpen : null, NO_RUNS);
  const closed = useWatch(live ? startClosed : null, NO_RUNS);
  const custody = useWatch(live ? startCustody : null, NO_CUSTODY);

  const runs = useMemo(() => {
    const byId = new Map<string, OpsRun>();
    for (const r of [...closed.data, ...open.data]) byId.set(r.id, r);
    return [...byId.values()]
      .map((r) => overlayRun(r, entries))
      .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
  }, [open.data, closed.data, entries]);

  // Stops are kept for the runs a driver works now or tomorrow (the offline working set).
  const workingRunIds = useMemo(
    () => open.data.filter((r) => r.date <= tomorrow && r.status !== 'closed').map((r) => r.id),
    [open.data, tomorrow],
  );
  const stops = useKeyedWatch<OpsStop[]>(workingRunIds, (runId, d, e) =>
    watchRunStops(db, runId, d, e),
  );
  // The boxes on my stops (the rules let a driver query them by `plannedDriverUid`): what should be on the van.
  const startBoxes = useCallback(
    (d: (v: RunBox[]) => void, e: (err: Error) => void) => watchMyBoxes(db, uid ?? '', d, e),
    [db, uid],
  );
  const boxes = useWatch(live && workingRunIds.length > 0 ? startBoxes : null, NO_BOXES);

  const stopViews = useMemo(() => {
    const out = new Map<string, StopView[]>();
    for (const [runId, list] of stops.data) out.set(runId, overlayStops(list, entries));
    return out;
  }, [stops.data, entries]);

  const shipmentIds = useMemo(
    () =>
      [...stops.data.values()].flatMap((list) =>
        list.flatMap((s) => (s.kind === 'delivery' && s.shipmentId ? [s.shipmentId] : [])),
      ),
    [stops.data],
  );
  const pickupIds = useMemo(
    () =>
      [...stops.data.values()].flatMap((list) =>
        list.flatMap((s) => (s.kind === 'pickup' && s.pickupRequestId ? [s.pickupRequestId] : [])),
      ),
    [stops.data],
  );
  const shipments = useDocMap<OpsShipment>('shipments', shipmentIds, 'trackingNo', NO_SHIPMENTS);
  const pickups = useDocMap<OpsPickupRequest>('pickupRequests', pickupIds, 'id', NO_PICKUPS);

  const hasPickupWork = canPickup && pickupIds.length > 0;
  const config = usePickupConfig(hasPickupWork);

  const stopsOf = useCallback((runId: string) => stopViews.get(runId), [stopViews]);
  const boxesOf = useCallback(
    (runId: string) => boxes.data.filter((b) => b.runId === runId),
    [boxes.data],
  );
  const problemsOf = useCallback((runId: string) => runProblems(runId, entries), [entries]);

  const value = useMemo<DriverData>(
    () => ({
      isDriver,
      today,
      runs,
      loading: live && (open.loading || closed.loading),
      error: open.error ?? closed.error ?? stops.error,
      stopsOf,
      boxesOf,
      shipments,
      pickups,
      custody: custody.data,
      problemsOf,
      hasActiveRun: runs.some((r) => r.status === 'active'),
      config,
    }),
    [
      isDriver,
      today,
      runs,
      live,
      open.loading,
      open.error,
      closed.loading,
      closed.error,
      stops.error,
      stopsOf,
      boxesOf,
      shipments,
      pickups,
      custody.data,
      problemsOf,
      config,
    ],
  );

  return <DriverContext.Provider value={value}>{children}</DriverContext.Provider>;
}
